import Image from 'next/future/image';
import { Dialog } from '@headlessui/react';
import React, { useState } from 'react';
import { Customer } from '@lambda/apis/src/customer/types';
import Link from 'next/link';
import logoMobile from '@/public/images/reebelo_text_logo.svg';
import logoWheelleft from '@/public/images/logo_wheel_left.svg';
import logoWheelright from '@/public/images/logo_wheel_right.svg';
import RegisterForm from './RegisterForm';
import i18n from '@/settings/i18n';
import LoginForm from './LoginForm';
import CustomerModalHeader from './CustomerModalHeader';
import AccountIcon from '@/components/accounts/AccountIcon';
import { CustomerFormType } from './types';

const t = i18n.init();

export default function CustomerModal(props: {
  open: boolean;
  formType: CustomerFormType;
  header?: string;
  onClose: (e?: any) => void; // note that an event e can be passed. If it is, the propagation will be stopped to avoid side effects.
  onSuccess: (customer: Customer) => void;
}) {
  const [showLoginForm, setShowLoginForm] = useState(
    props.formType === CustomerFormType.LOGIN,
  );

  return (
    <Dialog
      as="div"
      className="fixed inset-0 z-50 flex h-[100dvh] w-full items-start justify-center overflow-auto bg-teal-500 px-5 pt-16 md:items-center md:bg-gray-700/[.5] md:pt-0"
      onClose={props.onClose}
      open={props.open}
    >
      <div className="w-full text-center md:w-auto">
        <Dialog.Overlay className="fixed inset-0 z-0" />

        <button
          onClick={props.onClose}
          type="button"
          className="btn-close absolute right-5 top-5 opacity-100 md:right-4 md:top-4 md:hidden md:h-2 md:w-2"
          aria-label="Close"
        />
        <div className="absolute left-2/4 top-[18px] translate-x-[-50%] md:hidden">
          <Image src={logoMobile} loading="eager" alt="reebelo" />
        </div>

        <div
          className="relative z-30 mx-auto w-full max-w-[520px] rounded-[5px] bg-white px-6 py-5 shadow-xl md:px-20 md:py-10"
          id="e2e-registration-modal"
        >
          <button
            onClick={props.onClose}
            type="button"
            className="btn-close absolute right-5 top-5 hidden opacity-100 md:right-4 md:top-4 md:block md:h-2 md:w-2"
            aria-label="CloseModal"
          />
          {props.header && <CustomerModalHeader header={props.header} />}
          {showLoginForm ? (
            <>
              <LoginForm
                onSuccess={props.onSuccess}
                header={
                  <h1 className="mb-2 flex flex-col items-center justify-center text-xl font-bold text-gray-700 md:mb-4 md:flex-row md:text-[1.875rem]">
                    <AccountIcon />
                    {t`Login to my account`}
                  </h1>
                }
              />
              <div className="mt-5 md:mt-8">
                <div className="mb-2 flex justify-center text-xs text-gray-700 md:text-[1rem]">
                  {t`New Customer`}?&nbsp;
                  <a
                    id="e2e-customer-modal-signup"
                    className="ml-1 text-blue-500 duration-300 ease-in-out"
                    href=""
                    onClick={(e) => {
                      e.preventDefault();
                      setShowLoginForm(false);
                    }}
                  >{t`Create your account`}</a>
                </div>
                <div
                  className="flex justify-center text-xs text-gray-700 md:text-[1rem]"
                  onClick={props.onClose}
                >
                  {t`Lost password`}?&nbsp;
                  <Link href={'/account/recover'}>
                    <a
                      id="e2e-login-modal-recover-account"
                      className="text-blue-500 duration-300 ease-in-out"
                    >
                      {t`Recover password`}
                    </a>
                  </Link>
                </div>
              </div>
            </>
          ) : (
            <>
              <RegisterForm
                onSuccess={props.onSuccess}
                header={
                  <h1 className="mb-2 flex flex-row items-center justify-center text-xl font-bold text-gray-700 md:mb-4 md:text-[1.875rem]">
                    <AccountIcon />
                    {t`Create my account`}
                  </h1>
                }
              />
              <div className="mt-3 text-center text-sm text-gray-700 md:text-[1rem]">
                {t`Already a member? Log-in`}
                <a
                  id="e2e-customer-modal-login"
                  className="ml-1 text-blue-500 duration-300 ease-in-out"
                  href=""
                  onClick={(e) => {
                    e.preventDefault();
                    setShowLoginForm(true);
                  }}
                >
                  {t`here.`}
                </a>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="absolute left-0 top-0 hidden lg:block">
        <Image
          height={104}
          width={208}
          alt="logoWheelleft"
          src={logoWheelleft}
        />
      </div>
      <div className="absolute bottom-0 right-0 hidden leading-[0] lg:block">
        <Image
          height={392}
          width={241}
          alt="logoWheelright"
          src={logoWheelright}
        />
      </div>
    </Dialog>
  );
}
